import { useState } from "react";

export interface SortState {
  column: string | null;
  direction: "asc" | "desc" | null;
}

export interface UseAdminListStateOptions {
  limit: number;
}

export interface UseAdminListStateResult<Row> {
  search: string;
  sortState: SortState;
  sortType: string;
  offset: number;
  activePage: number;
  selectedRows: Row[];
  setSelectedRows: (rows: Row[]) => void;
  handleSearch: (value: string) => void;
  handleSearchChange: (value: string) => void;
  handleSort: (column: string, direction: "asc" | "desc") => void;
  handlePageChange: (page: number) => void;
}

/**
 * Shared search/sort/pagination/selection state for admin list screens built on
 * `useListQuery` + `AdminList`/`AdminListToolbar`/`DataTableV2`. Extracted from
 * `AnnouncementList` (the first screen migrated to this shape) so the next admin
 * list migration doesn't re-implement the same handful of handlers.
 *
 * Deliberately has no knowledge of any specific API or row shape - it only owns
 * the UI-state side of the screen. Data fetching stays in the feature's own
 * `useXList`/`useListQuery` call, wired to this hook's `offset`/`search`/`sortType`.
 */
export function useAdminListState<Row>({ limit }: UseAdminListStateOptions): UseAdminListStateResult<Row> {
  const [search, setSearch] = useState("");
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null });
  const [offset, setOffset] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  const sortType = sortState.column && sortState.direction ? `${sortState.column}:${sortState.direction.toUpperCase()}` : "";

  const handleSearch = (value: string) => {
    setSearch(value);
    setOffset(0);
    setActivePage(1);
    setSelectedRows([]);
  };

  const handleSearchChange = (value: string) => {
    if (value === "") {
      setSearch("");
      setOffset(0);
      setActivePage(1);
      setSortState({ column: null, direction: null });
      setSelectedRows([]);
    }
  };

  const handleSort = (column: string, direction: "asc" | "desc") => {
    setSortState({ column, direction });
    setSelectedRows([]);
  };

  const handlePageChange = (page: number) => {
    const newOffset = page <= 1 ? 0 : (page - 1) * limit;
    setOffset(newOffset);
    setActivePage(page);
    setSelectedRows([]);
  };

  return {
    search,
    sortState,
    sortType,
    offset,
    activePage,
    selectedRows,
    setSelectedRows,
    handleSearch,
    handleSearchChange,
    handleSort,
    handlePageChange,
  };
}
